import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useSearchClient } from '../../hooks/useSearchClient';
import { useProductClassificationChildrenQuery } from '../../queries/__generated__/productClassificationChildren.generated';
import { DiscoveryView, discoveryViewAtom } from '../state';
import { FamilyFacet, familyFacetsForParent, familyLevelLabelKey, fetchFamilyLevelFacets, sortFamilyFacets } from '../navigation/family-level-facets';

export interface UseFamilyLevelFacets {
  items: FamilyFacet[];
  isLoading: boolean;
}

function indexForView(view: DiscoveryView): string {
  switch (view) {
    case 'PRODUCTS':
      return import.meta.env.REACT_APP_ALGOLIA_PACKAGING_INDEX;
    case 'SUPPLIERS':
      return import.meta.env.REACT_APP_ALGOLIA_COMPANY_INDEX;
  }
}

export default function useFamilyLevelFacets(parents: string[]): UseFamilyLevelFacets {
  const searchClient = useSearchClient();
  const discoveryView = useAtomValue(discoveryViewAtom);
  const { t } = useTranslation(['product-family', 'product-classification']);
  const indexName = indexForView(discoveryView);
  const parentsKey = parents.join('\0');
  const parentCode = parents[parents.length - 1];
  const restrictToParent = discoveryView === 'SUPPLIERS' && !!parentCode;
  const { data: childrenData, loading: childrenLoading } = useProductClassificationChildrenQuery({
    variables: { parentCode: parentCode },
    skip: !restrictToParent,
  });
  const [loaded, setLoaded] = useState<{ key: string; items: FamilyFacet[] } | null>(null);

  //If the parents have changed, reset the loaded state
  if (loaded && loaded.key !== parentsKey) {
    setLoaded(null);
  }

  const facetsReady = loaded?.key === parentsKey && (!restrictToParent || !childrenLoading)
    ? loaded
    : null;

  useEffect(() => {
    let cancelled = false;

    fetchFamilyLevelFacets(searchClient, indexName, parents, (code) => t(familyLevelLabelKey(code, parents.length)))
      .then((next) => {
        if (!cancelled) {
          setLoaded({ key: parentsKey, items: next });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoaded({ key: parentsKey, items: [] });
        }
      });

    // Cancel the effect if the component unmounts or the parents have changed
    return () => {
      cancelled = true;
    };
  }, [indexName, parentsKey]);

  // eslint-disable-next-line no-nested-ternary
  const facets = facetsReady
    ? (restrictToParent
      ? familyFacetsForParent(facetsReady.items, childrenData?.product_classification_node.map(({ code }) => code) || [])
      : facetsReady.items)
    : [];

  return {
    items: sortFamilyFacets(facets, (code) => t(familyLevelLabelKey(code, parents.length))),
    isLoading: !facetsReady,
  };
}
